
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Music, 
  Heart, 
  Users, 
  BarChart2,
  DollarSign, 
  Library, 
  ShoppingBag, 
  Calendar, 
  FileText, 
  Video,
  ShoppingCart,
  Award,
  Zap,
  Radio,
  Headphones,
  Coins
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export interface FeatureLinkItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  highlight?: boolean;
}

interface FeatureLinksProps {
  links?: FeatureLinkItem[];
}

export const defaultFeatureLinks: FeatureLinkItem[] = [
  { path: '/calendar-scheduling', label: 'Calendar & Scheduling', icon: <Calendar className="h-5 w-5" />, color: 'bg-sky-700 text-white', highlight: true },
  { path: '/funding', label: 'Artist Funding', icon: <Coins className="h-5 w-5" />, color: 'bg-green-700 text-white', highlight: true },
  { path: '/sync', label: 'Sync Licensing', icon: <Zap className="h-5 w-5" />, color: 'bg-amber-700 text-white' },
  { path: '/releases', label: 'Releases', icon: <Music className="h-5 w-5" />, color: 'bg-blue-700 text-white' },
  { path: '/musicians-network', label: 'Find Collaborators', icon: <Heart className="h-5 w-5" />, color: 'bg-pink-700 text-white' },
  { path: '/analytics', label: 'Analytics', icon: <BarChart2 className="h-5 w-5" />, color: 'bg-purple-700 text-white' },
  { path: '/royalties', label: 'Royalties', icon: <DollarSign className="h-5 w-5" />, color: 'bg-emerald-700 text-white' },
  { path: '/library', label: 'Library', icon: <Library className="h-5 w-5" />, color: 'bg-indigo-700 text-white' },
  { path: '/marketplace', label: 'Marketplace', icon: <ShoppingBag className="h-5 w-5" />, color: 'bg-orange-700 text-white' },
  { path: '/legal-tools', label: 'Legal Tools', icon: <FileText className="h-5 w-5" />, color: 'bg-teal-700 text-white' },
  { path: '/live-streaming', label: 'Live Streaming', icon: <Video className="h-5 w-5" />, color: 'bg-cyan-700 text-white' },
  { path: '/radio', label: 'Radio', icon: <Radio className="h-5 w-5" />, color: 'bg-red-700 text-white' },
  { path: '/award-networking', label: 'Award Networking', icon: <Award className="h-5 w-5" />, color: 'bg-rose-700 text-white' },
];

const FeatureLinks: React.FC<FeatureLinksProps> = ({ links = defaultFeatureLinks }) => {
  return (
    <Card className="mb-6 animate-slide-up">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl">Features & Tools</CardTitle>
        <CardDescription>Quick access to all platform features</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {links.map((link) => (
            <Link key={link.path} to={link.path} className="no-underline">
              <div className={`rounded-lg p-3 hover:scale-105 transition-transform ${link.color} ${link.highlight ? 'ring-2 ring-offset-2 ring-sky-500 shadow-lg' : ''}`}>
                <div className="flex flex-col items-center text-center gap-2">
                  {link.icon}
                  <span className="text-sm font-medium">{link.label}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default FeatureLinks;
